'use client';

import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type AppLocale } from "@/i18n/locales";
import { logoutUser, deleteAccount } from "../services/authService";
import { AUTH_STATE_CHANGED_EVENT, authStorage } from "../lib/authStorage";
import { getUserDisplayName } from "../lib/userDetails";
import { userService } from "../services/services";
import type { UserDetails } from "../types/domain";
import NewModal from "./NewModal";
import { useIdleTimer } from "../hooks/useIdleTimer";
import MobileTopBar from "./MobileTopBar";
import { NAV_ITEMS, NAV_OFFSET, NavLabelKey } from "../constants";
import LanguageDropdown from "./LanguageDropdown";
import DesktopTopBar from "./DesktopTopBar";
import ConfirmationModal from "./ConfirmationModal";


export default function TopBar() {
  const t = useTranslations("topbar");
  const router = useRouter();
  const pathname = usePathname();
  const currentLocale = useLocale();
  const [hasUserId, setHasUserId] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [phase, setPhase] = useState<0 | 1 | 2>(0);
  const [showFullBar, setShowFullBar] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const clickLockRef = useRef(false);

  useEffect(() => {
    const syncUserIdState = () => {
      setHasUserId(authStorage.hasSession());
      setUserDetails(authStorage.getUserDetails<UserDetails>());
    };

    syncUserIdState();
    window.addEventListener("storage", syncUserIdState);
    window.addEventListener(AUTH_STATE_CHANGED_EVENT, syncUserIdState);

    return () => {
      window.removeEventListener("storage", syncUserIdState);
      window.removeEventListener(AUTH_STATE_CHANGED_EVENT, syncUserIdState);
    };
  }, []);

  useEffect(() => {
    if (!hasUserId) {
      return;
    }

    const userId = authStorage.getUserId();
    if (!userId) {
      return;
    }

    let isMounted = true;

    userService
      .getUserDetails(userId)
      .then((user) => {
        if (!isMounted) return;
        authStorage.setUserDetails(user);
        setUserDetails(user);
      })
      .catch(() => {
        if (!isMounted) return;
        setUserDetails(authStorage.getUserDetails<UserDetails>());
      });

    return () => {
      isMounted = false;
    };
  }, [hasUserId]);

  useEffect(() => {
    const t1 = window.setTimeout(() => setPhase(1), 80);
    const t2 = window.setTimeout(() => setPhase(2), 900);
    const t3 = window.setTimeout(() => setShowFullBar(true), 1150);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, []);

  useEffect(() => {
    if (!isLangOpen && !isMenuOpen && !isProfileOpen && !mobileNavOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const langElements = document.querySelectorAll("[data-lang-dropdown]");
      const menuElements = document.querySelectorAll("[data-menu-dropdown], [data-menu-button]");
      const profileElements = document.querySelectorAll("[data-profile-dropdown], [data-profile-button]");
      const mobileNavElements = document.querySelectorAll("[data-mobile-nav]");
      let isInside = false;

      langElements.forEach((element) => {
        if (element.contains(target)) {
          isInside = true;
        }
      });
      menuElements.forEach((element) => {
        if (element.contains(target)) {
          isInside = true;
        }
      });
      profileElements.forEach((element) => {
        if (element.contains(target)) {
          isInside = true;
        }
      });
      mobileNavElements.forEach((element) => {
        if (element.contains(target)) {
          isInside = true;
        }
      });

      if (!isInside) {
        setIsLangOpen(false);
        setIsMenuOpen(false);
        setIsProfileOpen(false);
        setMobileNavOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isLangOpen, isMenuOpen, isProfileOpen, mobileNavOpen]);

  const cleanPath = useMemo(() => {
    if (pathname.startsWith(`/${currentLocale}/`)) {
      return pathname.slice(`/${currentLocale}`.length);
    }

    if (pathname === `/${currentLocale}`) {
      return "/";
    }

    return pathname;
  }, [pathname, currentLocale]);

  const isPastTransactionsPage = cleanPath === "/pastTransaction";
  const pastTransactionsHref =
    currentLocale === "en" ? "/pastTransaction" : `/${currentLocale}/pastTransaction`;

  const switchLanguage = useCallback(
    (locale: AppLocale) => {
      if (locale === currentLocale) {
        setIsLangOpen(false);
        return;
      }

      document.cookie = `NEXT_LOCALE=${locale}; path=/; max-age=31536000`;
      setIsLangOpen(false);
      router.push(locale === "en" ? cleanPath : `/${locale}${cleanPath}`);
    },
    [cleanPath, currentLocale, router]
  );

  const [activeSection, setActiveSection] = useState<NavLabelKey>("productCatalog");

  useEffect(() => {
    const handleScroll = () => {
      if (clickLockRef.current) {
        return;
      }

      let current: NavLabelKey = "productCatalog";
      for (let index = NAV_ITEMS.length - 1; index >= 0; index -= 1) {
        const element = document.getElementById(NAV_ITEMS[index].sectionId);
        if (element && element.getBoundingClientRect().top <= NAV_OFFSET) {
          current = NAV_ITEMS[index].labelKey;
          break;
        }
      }

      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  const scrollToSection = useCallback(
    (sectionId: string, labelKey: NavLabelKey, shouldCloseMobileNav?: boolean) => {
      if (cleanPath === "/") {
        const target = document.getElementById(sectionId);
        if (!target) {
          return;
        }

        const top = target.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
        setActiveSection(labelKey);
        if (shouldCloseMobileNav) {
          closeMobileNav();
        }
        clickLockRef.current = true;
        window.scrollTo({ top, behavior: "smooth" });
        window.setTimeout(() => {
          clickLockRef.current = false;
        }, 1000);
        return;
      }

      const localePrefix = currentLocale === "en" ? "" : `/${currentLocale}`;
      router.push(`${localePrefix}/#${sectionId}`);
      if (shouldCloseMobileNav) {
        closeMobileNav();
      }
    },
    [cleanPath, closeMobileNav, currentLocale, router]
  );

  const handleLogoClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (cleanPath === "/") {
        event.preventDefault();
        closeMobileNav();
        setIsLangOpen(false);
        setIsProfileOpen(false);
        setActiveSection("productCatalog");
        clickLockRef.current = true;
        window.scrollTo({ top: 0, behavior: "smooth" });
        window.setTimeout(() => {
          clickLockRef.current = false;
        }, 1000);
      } else {
        closeMobileNav();
      }
    },
    [cleanPath, closeMobileNav]
  );

  const handleLogout = useCallback(() => {
    logoutUser()
      .catch(() => null)
      .finally(() => {
        authStorage.clearSession();
        authStorage.notifyAuthChanged();
        setHasUserId(false);
        setUserDetails(null);
        if (isPastTransactionsPage) {
          const homeHref = currentLocale === "en" ? "/" : `/${currentLocale}/`;
          window.location.href = homeHref;
          return;
        }
        window.location.reload();
      });
  }, [currentLocale, isPastTransactionsPage]);

  const triggerLogoutConfirm = useCallback(() => {
    setLogoutConfirmOpen(true);
  }, []);

  const triggerDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDeleteAccount = useCallback(() => {
    setIsDeleting(true);

    deleteAccount()
      .then(() => {
        setDeleteConfirmOpen(false);
        authStorage.clearSession();
        authStorage.notifyAuthChanged();
        setHasUserId(false);
        setUserDetails(null);

        if (isPastTransactionsPage) {
          const homeHref = currentLocale === "en" ? "/" : `/${currentLocale}`;
          router.push(homeHref);
        }
      })
      .catch(() => {
        // Error toast is already handled by internalApiClient
      })
      .finally(() => {
        setIsDeleting(false);
      });
  }, [currentLocale, isPastTransactionsPage, router]);


  useIdleTimer(handleLogout, hasUserId);

  const isDenseLocale = currentLocale === "bn" || currentLocale === "as";

  const navButtonClass = (labelKey: NavLabelKey) =>
    `topbar-nav-link rounded-full font-bold transition-colors duration-150 cursor-pointer whitespace-nowrap ${
      isDenseLocale
        ? "px-1.5 py-0.5 text-[10px] lg:px-2 lg:py-1 lg:text-xs"
        : "px-2 py-1 lg:px-3 lg:py-1.5 text-xs lg:text-sm"
    } ${
      activeSection === labelKey
        ? "bg-[#0A2540] text-white"
        : "text-[#334155] hover:bg-slate-100 hover:text-[#0A2540]"
    }`;

  const mobileNavButtonClass = (labelKey: NavLabelKey) =>
    `px-3 py-2 rounded-2xl text-sm font-medium transition-colors duration-150 cursor-pointer text-left ${
      activeSection === labelKey
        ? "bg-[#0A2540] text-white"
        : "text-[#334155] hover:bg-slate-100 hover:text-[#0A2540]"
    }`;

  const desktopPillStyle = (): React.CSSProperties => {
    if (phase === 0) {
      return {
        width: "56px",
        borderRadius: "28px",
        transform: "translateY(-100px)",
        transition: "transform 0.7s cubic-bezier(0.65,0,0.35,1)",
      };
    }

    if (phase === 1) {
      return {
        width: "56px",
        borderRadius: "28px",
        transform: "translateY(0)",
        transition: "transform 0.7s cubic-bezier(0.65,0,0.35,1)",
      };
    }

    return {
      width: "100%",
      borderRadius: "9999px",
      transform: "translateY(0)",
      transition: "width 0.65s cubic-bezier(0.65,0,0.35,1), border-radius 0.4s ease",
    };
  };

  const userDisplayName = (() => {
    const name = getUserDisplayName(userDetails) || t("profileGuest");
    const normalized = name.charAt(0).toUpperCase() + name.slice(1);
    return normalized.length > 10 ? `${normalized.slice(0, 7)}...` : normalized;
  })();
  const isPremiumUser = Boolean(userDetails?.isPremium);
  const mobileHeaderHeight = hasUserId && mobileNavOpen ? 104 : 56;
  const mobileExpandedHeight = mobileNavOpen
    ? `${(NAV_ITEMS.length + (hasUserId ? 1 : 0)) * 44 + mobileHeaderHeight + 18}px`
    : `${mobileHeaderHeight}px`;

  return (
    <div className="fixed top-0 left-0 right-0 z-9999 flex justify-center pt-4 px-4 pointer-events-none">
      <DesktopTopBar
        desktopPillStyle={desktopPillStyle}
        isDenseLocale={isDenseLocale}
        phase={phase}
        showFullBar={showFullBar}
        handleLogoClick={handleLogoClick}
        scrollToSection={scrollToSection}
        navButtonClass={navButtonClass}
        setIsLangOpen={setIsLangOpen}
        isLangOpen={isLangOpen}
        currentLocale={currentLocale}
        switchLanguage={switchLanguage}
        hasUserId={hasUserId}
        pastTransactionsHref={pastTransactionsHref}
        isPastTransactionsPage={isPastTransactionsPage}
        userDisplayName={userDisplayName}
        handleLogout={triggerLogoutConfirm}
        setIsMenuOpen={setIsMenuOpen}
        isMenuOpen={isMenuOpen}
        setLoginOpen={setLoginOpen}
        activeSection={activeSection}
        isProfileOpen={isProfileOpen}
        setIsProfileOpen={setIsProfileOpen}
        onDeleteAccount={triggerDeleteConfirm}
        isPremiumUser={isPremiumUser}
      />
      <MobileTopBar
        t={t}
        phase={phase}
        hasUserId={hasUserId}
        mobileNavOpen={mobileNavOpen}
        isLangOpen={isLangOpen}
        isPastTransactionsPage={isPastTransactionsPage}
        pastTransactionsHref={pastTransactionsHref}
        userDisplayName={userDisplayName}
        isPremiumUser={isPremiumUser}
        mobileExpandedHeight={mobileExpandedHeight}
        setMobileNavOpen={setMobileNavOpen}
        setIsLangOpen={setIsLangOpen}
        setLoginOpen={setLoginOpen}
        handleLogout={triggerLogoutConfirm}
        handleLogoClick={handleLogoClick}
        scrollToSection={scrollToSection}
        navItems={NAV_ITEMS}
        mobileNavButtonClass={mobileNavButtonClass}
        onDeleteAccount={triggerDeleteConfirm}
      />

      <div
        data-lang-dropdown
        className="lg:hidden absolute z-99999"
        style={{
          top: "calc(56px + 1rem + 8px)",
          right: "4rem",
          width: "160px",
          opacity: isLangOpen ? 1 : 0,
          transform: isLangOpen ? "translateY(0) scale(1)" : "translateY(-6px) scale(0.97)",
          pointerEvents: isLangOpen ? "auto" : "none",
          transition: "opacity 0.2s ease, transform 0.2s cubic-bezier(0.65,0,0.35,1)",
          transformOrigin: "top right",
        }}
      >
        <LanguageDropdown currentLocale={currentLocale} onSwitch={switchLanguage} t={t} />
      </div>

      <div className="pointer-events-auto">
        <NewModal
          isOpen={loginOpen}
          onClose={() => setLoginOpen(false)}
          onSuccess={() => {
            setLoginOpen(false);
            setHasUserId(true);
            authStorage.notifyAuthChanged();
          }}
        />
        <ConfirmationModal
          isOpen={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={handleConfirmDeleteAccount}
          title={t("deleteAccount")}
          message="Are you sure you want to delete your account? This action cannot be undone and you will lose access to all your past transactions and account details."
          confirmText="Delete Account"
          cancelText="Cancel"
          isDestructive={true}
          isLoading={isDeleting}
        />
        <ConfirmationModal
          isOpen={logoutConfirmOpen}
          onClose={() => setLogoutConfirmOpen(false)}
          onConfirm={() => {
            setLogoutConfirmOpen(false);
            handleLogout();
          }}
          title={t("profileLogout")}
          message={t("logoutConfirmation")}
          confirmText={t("profileLogout")}
          cancelText={t("cancel")}
          isDestructive={false}
        />
      </div>
    </div>
  );
}